import React, { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  TablePagination,
  CircularProgress,
  Alert,
  Chip,
  IconButton
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { tenantApiFetch } from './tenantApi';
import { TenantCan } from '../components/tenant/TenantCan';

const STATUSES = ['queued', 'running', 'completed', 'failed', 'cancelled'];

function statusColor(s) {
  if (s === 'completed') return 'success';
  if (s === 'failed') return 'error';
  if (s === 'running') return 'info';
  if (s === 'cancelled') return 'default';
  return 'warning';
}

export default function TenantExportJobsListPage() {
  const navigate = useNavigate();
  const [qInput, setQInput] = useState('');
  const [appliedQ, setAppliedQ] = useState('');
  const [statusInput, setStatusInput] = useState('');
  const [appliedStatus, setAppliedStatus] = useState('');
  const [jobTypeInput, setJobTypeInput] = useState('');
  const [appliedJobType, setAppliedJobType] = useState('');
  const [fromInput, setFromInput] = useState('');
  const [appliedFrom, setAppliedFrom] = useState('');
  const [toInput, setToInput] = useState('');
  const [appliedTo, setAppliedTo] = useState('');
  const [sort, setSort] = useState('created_at');
  const [order, setOrder] = useState('desc');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const params = new URLSearchParams({
      page: String(page + 1),
      pageSize: String(pageSize),
      sort,
      order
    });
    if (appliedQ) params.set('q', appliedQ);
    if (appliedStatus) params.set('status', appliedStatus);
    if (appliedJobType) params.set('jobType', appliedJobType);
    if (appliedFrom) params.set('fromDate', appliedFrom);
    if (appliedTo) params.set('toDate', appliedTo);
    try {
      const res = await tenantApiFetch(`/api/tenant/export-jobs?${params}`);
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.error || 'Failed to load export jobs');
      setData(json);
    } catch (e) {
      setError(e.message);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, appliedQ, appliedStatus, appliedJobType, appliedFrom, appliedTo, sort, order]);

  useEffect(() => {
    load();
  }, [load]);

  const applyFilters = () => {
    setPage(0);
    setAppliedQ(qInput.trim());
    setAppliedStatus(statusInput);
    setAppliedJobType(jobTypeInput);
    setAppliedFrom(fromInput);
    setAppliedTo(toInput);
  };

  const rows = data?.jobs || [];
  const total = data?.pagination?.total ?? 0;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" gutterBottom>
            Export jobs
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Request data exports, run processing, and download CSV outputs. Files are stored in tenant-isolated paths.
          </Typography>
        </Box>
        <TenantCan permission="tenant.exports.manage">
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate('/tenant/export-jobs/new')}>
            New export
          </Button>
        </TenantCan>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'flex-end' }}>
          <TextField
            size="small"
            label="Search"
            value={qInput}
            onChange={(e) => setQInput(e.target.value)}
            sx={{ minWidth: 180 }}
            onKeyDown={(e) => e.key === 'Enter' && applyFilters()}
          />
          <TextField
            size="small"
            label="Job type"
            value={jobTypeInput}
            onChange={(e) => setJobTypeInput(e.target.value)}
            sx={{ minWidth: 160 }}
          />
          <FormControl size="small" sx={{ minWidth: 130 }}>
            <InputLabel>Status</InputLabel>
            <Select label="Status" value={statusInput} onChange={(e) => setStatusInput(e.target.value)}>
              <MenuItem value="">All</MenuItem>
              {STATUSES.map((s) => (
                <MenuItem key={s} value={s}>
                  {s}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            size="small"
            label="From date"
            type="date"
            value={fromInput}
            onChange={(e) => setFromInput(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            size="small"
            label="To date"
            type="date"
            value={toInput}
            onChange={(e) => setToInput(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Sort</InputLabel>
            <Select label="Sort" value={sort} onChange={(e) => setSort(e.target.value)}>
              <MenuItem value="created_at">Created</MenuItem>
              <MenuItem value="completed_at">Completed</MenuItem>
              <MenuItem value="job_type">Job type</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 100 }}>
            <InputLabel>Order</InputLabel>
            <Select label="Order" value={order} onChange={(e) => setOrder(e.target.value)}>
              <MenuItem value="desc">Desc</MenuItem>
              <MenuItem value="asc">Asc</MenuItem>
            </Select>
          </FormControl>
          <Button variant="outlined" onClick={applyFilters}>
            Apply
          </Button>
        </Box>
      </Paper>

      <Paper sx={{ overflow: 'auto' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Type</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Requested by</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell>Completed</TableCell>
                  <TableCell align="right">View</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row) => (
                  <TableRow key={row.id} hover>
                    <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{row.job_type}</TableCell>
                    <TableCell>
                      <Chip size="small" label={row.status} color={statusColor(row.status)} variant="outlined" />
                    </TableCell>
                    <TableCell>{row.requested_by?.email || '—'}</TableCell>
                    <TableCell>{row.created_at ? String(row.created_at) : '—'}</TableCell>
                    <TableCell>{row.completed_at ? String(row.completed_at) : '—'}</TableCell>
                    <TableCell align="right">
                      <IconButton component={Link} to={`/tenant/export-jobs/${row.id}`} size="small">
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {rows.length === 0 && (
              <Box sx={{ p: 3 }}>
                <Typography color="text.secondary">No export jobs yet.</Typography>
              </Box>
            )}
            <TablePagination
              component="div"
              count={total}
              page={page}
              onPageChange={(_e, p) => setPage(p)}
              rowsPerPage={pageSize}
              onRowsPerPageChange={(e) => {
                setPageSize(Number(e.target.value));
                setPage(0);
              }}
              rowsPerPageOptions={[10, 25, 50]}
            />
          </>
        )}
      </Paper>
    </Box>
  );
}
